import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "351",
  "slug": "what-is-a-landlocked-country",
  "title": "What Is a Landlocked Country? The 44 Nations With No Coastline",
  "excerpt": "Forty-four countries have no access to the sea — and two are surrounded entirely by other landlocked countries. Here is what that means for trade, maps, and one very specific kind of trivia.",
  "date": "2026-06-04",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1780269197849-ee31eacad11f?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhbHBpbmUlMjBtb3VudGFpbiUyMHZhbGxleXxlbnwxfDB8fHwxNzgwNTYwNDgxfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "A landlocked country has no coastline. There are 44 of them. Two are 'doubly landlocked' — surrounded only by other landlocked countries: Liechtenstein and Uzbekistan.",
  "content": [
    {
      "type": "paragraph",
      "text": "A landlocked country is exactly what it sounds like: a nation with no coastline and therefore no direct access to the ocean. To reach the sea, its goods and ships have to cross at least one neighbor's territory first. It sounds like a minor quirk of geography, but it shapes trade, politics, and even the occasional pub-quiz win."
    },
    {
      "type": "heading",
      "text": "How Many Are There?"
    },
    {
      "type": "paragraph",
      "text": "There are 44 landlocked countries, and they are spread very unevenly across the globe. Africa has the most, with 16. Asia and Europe each hold a large cluster, while the Americas have just two and Australia and Oceania have none at all. The clustering matters: when landlocked countries border each other, reaching the sea can mean crossing several frontiers, each with its own paperwork and politics. A few of these borders are among the most heavily negotiated in the world precisely because so much trade has to squeeze across them."
    },
    {
      "type": "heading",
      "text": "Where They Cluster"
    },
    {
      "type": "list",
      "items": [
        "Africa (16) — including Ethiopia, Chad, Mali, Niger, Zambia, Zimbabwe, Uganda, and Rwanda.",
        "Asia — the Central Asian 'stans' (Kazakhstan, Uzbekistan, Turkmenistan, Tajikistan, Kyrgyzstan) plus Mongolia, Nepal, Bhutan, Afghanistan, and Laos.",
        "Europe — Switzerland, Austria, Hungary, the Czech Republic, Slovakia, Serbia, and the micro-states of Liechtenstein, Luxembourg, Andorra, San Marino, and Vatican City.",
        "The Americas — only Bolivia and Paraguay.",
        "Oceania — none at all."
      ]
    },
    {
      "type": "heading",
      "text": "Doubly Landlocked: The Rarest Club"
    },
    {
      "type": "paragraph",
      "text": "Now for the trivia. Only two countries on Earth are 'doubly landlocked', meaning every country they border is itself landlocked. Liechtenstein sits between Switzerland and Austria — both landlocked — and Uzbekistan is ringed by Kazakhstan, Kyrgyzstan, Tajikistan, Afghanistan, and Turkmenistan, none of which touch an ocean either. To get from either country to open sea, you must cross at least two international borders."
    },
    {
      "type": "heading",
      "text": "Why It Matters"
    },
    {
      "type": "paragraph",
      "text": "Being landlocked has real costs. Without a port of your own, you depend on neighbors and on transit agreements to move goods, which makes trade slower and more expensive. The United Nations even maintains a category, the Landlocked Developing Countries, to address those challenges. Politics can bite hard: when Eritrea became independent in 1993, Ethiopia lost its coastline overnight and now routes most of its trade through the port of neighboring Djibouti. That said, geography is not destiny — Switzerland, Austria, and Luxembourg are among the wealthiest nations on the planet despite never seeing a wave."
    },
    {
      "type": "heading",
      "text": "The Scramble for Access"
    },
    {
      "type": "paragraph",
      "text": "Because a landlocked economy depends on its neighbors' goodwill, sea access becomes a permanent strand of foreign policy. Nepal funnels most of its trade through Indian ports and felt it sharply during past border blockades, when fuel and supplies dried up almost overnight. Many landlocked states negotiate 'dry ports' — inland cargo terminals linked by rail or road to a partner's coast — and dedicated transit corridors written into treaties. International law even grants landlocked countries a right of access to the sea in principle, but in practice it runs on bilateral deals, tariffs, and the reliability of whoever controls the route to the water. That dependence has shaped history more than once: blockades, transit fees, and closed borders have all been used as leverage against landlocked neighbors, and some of the world's longest-running diplomatic disputes are really arguments about a road or a railway to the coast."
    },
    {
      "type": "heading",
      "text": "Landlocked but Not Without Water"
    },
    {
      "type": "paragraph",
      "text": "Landlocked does not mean dry. Several of these countries border huge inland bodies of water — Kazakhstan sits on the Caspian Sea, the largest inland body of water in the world — but because the Caspian is not connected to the ocean, they still count as landlocked. A few even keep navies: Bolivia maintains a naval force on rivers and on Lake Titicaca, and still marks an annual Day of the Sea commemorating the Pacific coastline it lost to Chile in the 1800s."
    },
    {
      "type": "heading",
      "text": "A Geography-Quiz Favorite"
    },
    {
      "type": "paragraph",
      "text": "'Name a doubly landlocked country' and 'which continent has the most landlocked countries' are two of the most reliable stumpers in geography trivia — and now you have both. They are also some of the trickiest places to identify from imagery, since you cannot fall back on a coastline to anchor your guess and one stretch of inland farmland looks much like another. Test your inland instincts in a round of EarthGuessr."
    }
  ]
};
